// ChoiceOptionsEditor — the "Answer choices" editor a checkList field carries:
// a Response-set dropdown (ResponseSetPicker) over the optionList table
// (response · color · value rows, deletable, appendable). Extracted from
// FormBuilderBasicOptions so the conditional-logic drawer can edit a follow-up
// checkList's choices with the same control.
//
// Picking a response set COPIES its choices into optionList, replacing what's
// there; the table is the editable copy, which is what "fine-tune" means.
// The apply goes through `onApplyResponseSet`, NOT `onChange`: applying a set
// must kill ALL of the field's conditional-logic rules, and the caller can't
// detect an apply from an optionList diff (a set may share values with the
// current options). Colors are 8-digit hex; alpha 00 = "no color" (checkered
// swatch); picking a color makes it fully opaque.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton, Input } from '@/components/common';
import { emptyChoiceOption, type ChoiceOption } from './utils/formElementDefaults';
import { splitHex8 } from './utils/builderColors';
import ResponseSetPicker from './response-sets/ResponseSetPicker';

interface ChoiceOptionsEditorProps {
  options: ChoiceOption[];
  /** Row edits (add / patch / delete) — never rule-destructive. */
  onChange: (options: ChoiceOption[]) => void;
  /** A response set was applied — the caller replaces the optionList AND kills
   *  every conditional-logic rule on the field. */
  onApplyResponseSet: (options: ChoiceOption[]) => void;
}

const ChoiceOptionsEditor = ({ options, onChange, onApplyResponseSet }: ChoiceOptionsEditorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const patch = (i: number, part: Partial<ChoiceOption>) =>
    onChange(options.map((o, x) => (x === i ? { ...o, ...part } : o)));

  return (
    <section className='form-builder-props__answers'>
      <button
        type='button'
        className='form-builder-props__answers-head'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('forms_builder_props_answers')}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={15} />
      </button>

      {open && (
        <div className='form-builder-props__answers-body'>
          {/* Seed the options from a reusable response set; the table below is
           * the editable copy (hence "fine-tune"). */}
          <div className='form-builder-props__field-row'>
            <span className='form-builder-props__label'>
              {t('forms_builder_props_response_set')}
            </span>
          </div>
          <ResponseSetPicker onPick={onApplyResponseSet} />

          <div className='form-builder-props__field-row'>
            <span className='form-builder-props__label'>{t('forms_builder_props_options')}</span>
            <span className='form-builder-props__label-note'>
              {t('forms_builder_props_options_note')}
            </span>
          </div>

          <div className='form-builder-props__options-head' aria-hidden='true'>
            <span />
            <span>{t('forms_builder_props_col_response')}</span>
            <span>{t('forms_builder_props_color')}</span>
            <span>{t('forms_builder_props_col_value')}</span>
            <span />
          </div>

          {options.map((o, i) => {
            const { rgb, alpha } = splitHex8(o.itemColor);
            return (
              // Index keys are fine here — rows only append/remove in place.
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className='form-builder-props__option-row'>
                <span
                  className='form-builder-props__option-dot'
                  style={alpha > 0 ? { borderColor: rgb } : undefined}
                  aria-hidden='true'
                />
                <Input
                  size='sm'
                  value={o.responseText}
                  aria-label={t('forms_builder_props_col_response')}
                  onChange={(e) => patch(i, { responseText: e.target.value })}
                />
                <label
                  className={[
                    'form-builder-props__option-swatch',
                    alpha === 0 && 'form-builder-props__option-swatch--none',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={alpha > 0 ? { background: o.itemColor } : undefined}
                >
                  <input
                    type='color'
                    value={rgb}
                    aria-label={t('forms_builder_props_color')}
                    onChange={(e) => patch(i, { itemColor: `${e.target.value}ff` })}
                  />
                </label>
                <Input
                  size='sm'
                  className='form-builder-props__option-value'
                  inputMode='numeric'
                  value={o.value}
                  aria-label={t('forms_builder_props_col_value')}
                  onChange={(e) => patch(i, { value: e.target.value })}
                />
                <IconButton
                  icon='x'
                  variant='ghost'
                  size='sm'
                  className='form-builder-props__option-delete'
                  aria-label={t('forms_builder_props_delete_option')}
                  onClick={() => onChange(options.filter((_, x) => x !== i))}
                />
              </div>
            );
          })}

          <button
            type='button'
            className='form-builder-props__add-option'
            onClick={() => onChange([...options, emptyChoiceOption()])}
          >
            <Icon name='plus' size={15} />
            {t('forms_builder_props_add_option')}
          </button>
        </div>
      )}
    </section>
  );
};

export default ChoiceOptionsEditor;
